import * as R from 'ramda';

import { AssetCreateAttributesInput, ChangeType } from '@atom/types/asset';
import { Schema } from '@atom/types/schema';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

// Handles checking both valid versions fo the elements changed enum
export const isElementsChanged = (changeType?: string): boolean =>
  changeType === ChangeType.ELEMENTS_CHANGED ||
  changeType === ChangeType.ELEMENTS_CHANGED_LEGACY;

// Own change = the node itself was added/deleted/edited (or sits under a pending-new
// parent). Excludes elementsChanged, which only says descendants changed.
export const hasOwnChange = (changeType?: string): boolean =>
  Boolean(changeType) && !isElementsChanged(changeType);

export const formatAssetAttributeFilters = (filters?: object): object => {
  if (!filters) {
    return {};
  }

  return Object.entries(filters).reduce((accumulator, [id, value]) => {
    const key = `attributes.${id}`;

    if (!Array.isArray(value) && R.is(Object, value)) {
      const from = value.from ? Number(value.from) : null;
      const to = value.to ? Number(value.to) : null;
      const valueArray = [from, to];

      if (!from && !to) {
        return accumulator;
      }

      return {
        ...accumulator,
        [key]: valueArray,
      };
    }

    return R.isEmpty(value) ? accumulator : { ...accumulator, [key]: value };
  }, {});
};

export const getAttributeErrors = (
  schema: Schema,
  attributes: AssetCreateAttributesInput,
): Map<string, string> => {
  const requiredAttributeIds = R.toPairs(schema.attributes ?? {}).reduce<
    string[]
  >(
    (acc, [attributeId, attribute]) =>
      attribute.isRequired && isNilOrEmpty(attribute.defaultValue)
        ? [...acc, attributeId]
        : acc,
    [],
  );

  const filledOutAttributeIds = R.toPairs(attributes).reduce<string[]>(
    (acc, [attributeId, attribute]) =>
      isNilOrEmpty(attribute.value) ? acc : [...acc, attributeId],
    [],
  );

  const missingRequiredIds = R.difference(
    requiredAttributeIds,
    filledOutAttributeIds,
  );

  return new Map(missingRequiredIds.map(id => [id, 'Required']));
};

export const cleanAttributes = (
  attributes: AssetCreateAttributesInput,
): AssetCreateAttributesInput => {
  const keysWithNoValue = R.toPairs(attributes).reduce<string[]>(
    (acc, [attributeId, attribute]) =>
      isNilOrEmpty(attribute.value) ? [...acc, attributeId] : acc,
    [],
  );

  return R.omit(keysWithNoValue, attributes);
};
